<template>
  <transition name="fade-fast">
    <div v-if="novel">
      <div class="novel-detail">
        <div class="info-img">
          <div class="cover-image-wrapper" ref="coverImageWrapper">
            <image-view
              :key="coverImageVersion"
              :src="imageKey"
              class="novel-cover-image"
              placeholder="/img/placeholders/novel.png"
            />
            <button
              v-if="!hasCoverImage"
              type="button"
              class="cover-image-edit-button"
              :disabled="isCoverImageSaving"
              :aria-label="$t('novelDetail.changeCoverImage').toString()"
              @click="onClickChangeCoverImage"
            >
              <IconImagePlus class="cover-image-edit-icon" aria-hidden="true" />
            </button>
            <template v-else>
              <button
                type="button"
                ref="coverImageMenuButton"
                class="cover-image-edit-button"
                :disabled="isCoverImageSaving"
                aria-haspopup="true"
                :aria-expanded="isCoverImageMenuOpen ? 'true' : 'false'"
                :aria-label="$t('novelDetail.coverImageSetting.menuButtonLabel').toString()"
                @click="toggleCoverImageMenu"
              >
                <IconPencil class="cover-image-edit-icon" aria-hidden="true" />
              </button>
              <div v-if="isCoverImageMenuOpen" ref="coverImageMenu" role="menu" class="cover-image-menu-popover">
                <button
                  type="button"
                  role="menuitem"
                  class="cover-image-menu-item"
                  :disabled="isCoverImageSaving"
                  @click="onClickChangeCoverImage"
                >
                  <IconImagePlus class="cover-image-menu-item-icon" aria-hidden="true" />
                  <span>{{ $t("novelDetail.coverImageSetting.changeImage") }}</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  class="cover-image-menu-item cover-image-menu-item-danger"
                  :disabled="isCoverImageSaving"
                  @click="onClickDeleteCoverImage"
                >
                  <IconTrash class="cover-image-menu-item-icon" aria-hidden="true" />
                  <span>{{ $t("novelDetail.coverImageSetting.deleteImage") }}</span>
                </button>
              </div>
            </template>
          </div>
          <!-- 呼称の正常化により、UI上の「カテゴリ」はコード上のgenreフィールド、
               「ジャンル」はcategoryフィールドに対応する（ねじれているが意図通り） -->
          <div class="genre-category-card">
            <div class="genre-category-row">
              <h3 class="genre-category-label">{{ $t("novelDetail.categoryLabel") }}</h3>
              <button
                type="button"
                class="genre-category-edit-button"
                :disabled="isGenreCategorySaving"
                :aria-label="$t('novelDetail.editGenreCategory').toString()"
                @click="onClickEditGenreCategory"
              >
                <IconPencil class="genre-category-edit-icon" aria-hidden="true" />
              </button>
            </div>
            <div class="genre-category-value">{{ genreName || $t("novelDetail.genreCategoryUnset") }}</div>

            <h3 class="genre-category-label">{{ $t("novelDetail.genreLabel") }}</h3>
            <div v-if="categoryNames.length > 0" class="category-pills">
              <span v-for="item in categoryNames" :key="item.id" class="category-pill">{{ item.name }}</span>
            </div>
            <div v-else class="genre-category-value">{{ $t("novelDetail.genreCategoryUnset") }}</div>
          </div>
        </div>
        <div class="info-txt">
          <div class="work-status-wrapper" ref="workStatusWrapper">
            <button
              type="button"
              ref="workStatusButton"
              class="work-status-button"
              :disabled="isWorkStatusSaving"
              aria-haspopup="true"
              :aria-expanded="isWorkStatusMenuOpen ? 'true' : 'false'"
              :style="workStatusButtonStyle"
              @click="toggleWorkStatusMenu"
            >
              <span class="work-status-name">{{ workStatusName }}</span>
              <IconChevronDown class="work-status-chevron" aria-hidden="true" />
            </button>
            <div v-if="isWorkStatusMenuOpen" ref="workStatusMenu" role="menu" class="work-status-menu-popover">
              <button
                v-for="item in translatedWorkStatusList"
                :key="item.id"
                type="button"
                role="menuitemradio"
                class="work-status-menu-item"
                :disabled="isWorkStatusSaving"
                :aria-checked="currentWorkStatusId === item.id ? 'true' : 'false'"
                @click="onClickWorkStatus(item)"
              >
                <IconCheck
                  v-if="currentWorkStatusId === item.id"
                  class="work-status-menu-item-check"
                  aria-hidden="true"
                />
                <span v-else class="work-status-menu-item-check-spacer" aria-hidden="true" />
                <span>{{ item.name }}</span>
              </button>
            </div>
          </div>
          <div class="field-header">
            <h2>{{ $t("novelDetail.title") }}</h2>
            <span class="counter">{{ titleCounter }}</span>
          </div>
          <text-field
            v-model="editableTitle"
            :label="$t('novelDetail.title').toString()"
            :maxlength="titleMaxLength"
            sync-value
            @input="onChangeTitleOrDescription"
            @compositionstart.native="onCompositionStart('title')"
            @compositionend.native="onCompositionEnd('title')"
            class="detail-title-field"
          />
          <div class="field-header">
            <h2>{{ $t("novelDetail.synopsis") }}</h2>
            <span class="counter">{{ descriptionCounter }}</span>
          </div>
          <text-field
            v-model="editableDescription"
            :label="$t('novelDetail.synopsis').toString()"
            :rows="15"
            :maxlength="descriptionMaxLength"
            @input="onChangeTitleOrDescription"
            @compositionstart.native="onCompositionStart('description')"
            @compositionend.native="onCompositionEnd('description')"
            class="detail-description-field"
          />
        </div>
      </div>
      <div class="content">
        <hr class="dotted light-gray" />
        <div class="assosiate-container flex flex-column">
          <div>
            <h3 v-if="novelIdFromNolaNovel">{{ $t("novelDetail.nolaNovel.updateTitle") }}</h3>
            <h3 v-else>{{ $t("novelDetail.nolaNovel.postTitle") }}</h3>
          </div>
          <span v-if="novelIdFromNolaNovel">{{ $t("novelDetail.nolaNovel.updateDescription") }}</span>
          <template v-else>
            <span>{{ $t("novelDetail.nolaNovel.description") }}</span>
            <span>{{ $t("novelDetail.nolaNovel.editorInfo") }}</span>
            <span>{{ $t("novelDetail.nolaNovel.editorBenefit") }}</span>
            <span>{{ $t("novelDetail.nolaNovel.privacyNote") }}</span>
          </template>
          <div class="mt-1 flex flex-row gap-harf">
            <div class="btn post bold pointer" @click="postToNolaNovelButtonClick(true)">
              <span v-if="novelIdFromNolaNovel">{{ $t("novelDetail.nolaNovel.updateButton") }}</span>
              <span v-else>{{ $t("novelDetail.nolaNovel.postButton") }}</span>
            </div>
            <div class="btn link bold pointer" v-if="!novelIdFromNolaNovel" @click="postToNolaNovelButtonClick(false)">
              {{ $t("novelDetail.nolaNovel.linkButton") }}
            </div>
            <div class="btn link bold pointer" v-if="novelIdFromNolaNovel" @click="delinkNolaNovelButtonClick">
              {{ $t("novelDetail.nolaNovel.unlinkButton") }}
            </div>
          </div>
        </div>
        <hr class="dotted light-gray" />
        <div class="delete-container flex flex-column">
          <h3>{{ $t("novelDetail.delete.title") }}</h3>
          <span>{{ $t("novelDetail.delete.description") }}</span>
          <div class="mt-1 flex flex-row gap-harf">
            <button
              type="button"
              class="btn delete bold pointer"
              :disabled="isDeleteDisabled"
              @click="deleteButtonClick"
            >
              {{ $t("novelDetail.delete.deleteButton") }}
            </button>
          </div>
        </div>
      </div>
      <Onboarding
        v-if="showFirstWorkStatusHighlight"
        targetSelector=".work-status-wrapper .work-status-button"
        :message="`<div style='font-size:var(--font-size-md); margin-top:10px; margin-bottom:-12px; margin-right:55px'>${$t(
          'novelDetail.workStatusOnboardingMessage'
        )}</div>`"
        :canOutsideClick="true"
        :tooltipPosition="'bottom-right'"
        maxWidth="460px"
        tooltipPadding="14px 20px"
        tooltipBorderRadius="15px"
        @onboarding-finished="closeWorkStatusHighlight"
      />
    </div>
  </transition>
</template>
<script lang="ts">
import Vue from "vue";
import type { Component } from "vue-modal-dialogs";

import ImageView from "@/components/atoms/ImageView.vue";
import TextField from "@/components/molecules/TextField.vue";
import Onboarding from "@/components/molecules/OnBoarding.vue";
import {
  getTranslatedGenreList,
  getTranslatedCategoryList,
  getTranslatedWorkStatusList,
  getTranslatedGenreById,
  getTranslatedWorkStatusById,
  getCategoryNameItems,
  WORK_STATUS_COLORS,
  WORK_STATUS_HOVER_COLORS,
  WorkStatusId,
} from "@/lib/utils/novelSettingsHelper";
import { Novel, User } from "@/lib/models";
import NovelListDialog, { NovelListDialogProps } from "@/components/ui/nolaNovel/NovelListDialog.vue";
import { Dialog } from "@/lib/utils";
import NolaNovelUpdateConfirm, {
  NolaNovelUpdateConfirmProps,
} from "@/components/ui/nolaNovel/NolaNovelUpdateConfirm.vue";
import NolaNovelDelinkConfirm, {
  NolaNovelDelinkConfirmProps,
} from "@/components/ui/nolaNovel/NolaNovelDelinkConfirm.vue";
import CupertinoAlertDialog, { CupertinoAlertDialogProps } from "@/components/ui/dialogs/CupertinoAlertDialog.vue";
import { UserClient } from "@/lib/clients";
import { deleteBinary } from "@/lib/s3";
import NolaNovelIntroduction from "@/components/ui/nolaNovel/NolaNovelIntroduction.vue";
import NovelDeleteConfirm, { NovelDeleteConfirmProps } from "@/components/ui/novel/NovelDeleteConfirm.vue";
import CoverImageSettingDialog from "@/components/ui/novel/CoverImageSettingDialog.vue";
import CoverImageDeleteConfirmDialog from "@/components/ui/novel/CoverImageDeleteConfirmDialog.vue";
import GenreCategorySettingDialog from "@/components/ui/novel/GenreCategorySettingDialog.vue";
import { NolaNovelUrlGenerator } from "@/lib/utils/generator/nolanovelUrl";
import { createUrlWithUtmParams } from "@/lib/utils/url";
import { NolaItemId, NolaPageName } from "@/lib/utils/analytics";
import AgentPromotionDialog from "@/components/ui/AgentPromotionDialog.vue";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";
import IconImagePlus from "~icons/figma/ImagePlus";
import IconPencil from "~icons/figma/Pencil";
import IconTrash from "~icons/figma/Trash";
import IconChevronDown from "~icons/figma/ChevronDown";
import IconCheck from "~icons/figma/Check";

type NovelParam = {
  id: string;
  name: string;
  image: string;
};

const TITLE_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 10000;

function textCounter(length: number, max: number): string {
  return `${length.toLocaleString()}/${max.toLocaleString()}`;
}

// 表紙画像メニュー・制作状況メニューの「外側クリック判定」で共通利用する判定ロジック
function isOutsideWrapper(wrapper: HTMLElement | undefined, target: EventTarget | null): boolean {
  return !wrapper || !(target instanceof Node) || !wrapper.contains(target);
}

// 表紙画像メニュー・制作状況メニューで共通の項目取得・キーボード操作ロジック
// (GenreCategorySettingDialog.vueのフォーカストラップと操作性を揃える)
function getMenuItemElements(menu: HTMLElement | undefined): HTMLElement[] {
  if (!menu) return [];
  return Array.from(menu.querySelectorAll<HTMLElement>("button:not([disabled])"));
}

// ArrowUp/Down・Home/End での項目間移動と、Tabがメニュー外(背後のページ)へ
// 抜けないようにするループ処理。呼び出し側でEscapeは別途ハンドリングする
function handleMenuNavigationKeydown(event: KeyboardEvent, menu: HTMLElement | undefined): void {
  const items = getMenuItemElements(menu);
  if (items.length === 0) return;

  const currentIndex = items.indexOf(document.activeElement as HTMLElement);

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      items[(currentIndex + 1 + items.length) % items.length].focus();
      break;
    case "ArrowUp":
      event.preventDefault();
      items[(currentIndex - 1 + items.length) % items.length].focus();
      break;
    case "Home":
      event.preventDefault();
      items[0].focus();
      break;
    case "End":
      event.preventDefault();
      items[items.length - 1].focus();
      break;
    case "Tab":
      // 開いた直後などフォーカスがメニュー項目上にまだない場合は、既定のTab移動に任せる
      if (currentIndex === -1) break;
      if (event.shiftKey && currentIndex === 0) {
        event.preventDefault();
        items[items.length - 1].focus();
      } else if (!event.shiftKey && currentIndex === items.length - 1) {
        event.preventDefault();
        items[0].focus();
      }
      break;
    default:
      break;
  }
}

// メニューを開いた直後のフォーカス移動先を探す（現在選択中の項目があればそれを、なければ先頭を返す）
function getInitialMenuFocusTarget(menu: HTMLElement | undefined, selectedIndex: number): HTMLElement | undefined {
  const items = getMenuItemElements(menu);
  return items[selectedIndex] ?? items[0];
}

interface Data {
  hasNolaNovelAccount: boolean;
  editableTitle: string;
  editableDescription: string;
  initialTitle: string;
  initialDescription: string;
  isTitleDescriptionInitialized: boolean;
  isComposingTitle: boolean;
  isComposingDescription: boolean;
  savePromise: Promise<boolean> | null;
  isCoverImageMenuOpen: boolean;
  isCoverImageSaving: boolean;
  isGenreCategorySaving: boolean;
  isWorkStatusMenuOpen: boolean;
  isWorkStatusSaving: boolean;
  isNolaNovelDelinking: boolean;
  showFirstWorkStatusHighlight: boolean;
  unbindShortcutKeys: (() => void) | null;
}

type EditableField = "title" | "description";

interface Methods {
  initialize: () => void;

  deleteButtonClick: () => void;
  postToNolaNovelButtonClick: (isPost: boolean) => Promise<void>;
  delinkNolaNovelButtonClick: () => Promise<void>;

  showNolaNovelListDialog: (isPost: boolean) => void;
  showExportConfirmDialog: (id: string) => void;
  showNolaNovelDelinkConfirmDialog: (id: string) => Promise<void>;
  showNovelDeleteConfirmDialog: (title: string) => void;
  navigateToNolaNovelEditPage: (id: string) => void;
  onChangeTitleOrDescription: () => void;
  autoSaveTitleAndDescription: () => Promise<boolean>;
  onCompositionStart: (field: EditableField) => void;
  onCompositionEnd: (field: EditableField) => void;
  flushPendingChanges: () => Promise<boolean>;
  flushIfEditable: () => void;
  resumeAutoSaveIfNeeded: () => void;
  handleBeforeUnload: (event: BeforeUnloadEvent) => void;
  onClickChangeCoverImage: () => void;
  toggleCoverImageMenu: () => void;
  handleCoverImageMenuKeydown: (event: KeyboardEvent) => void;
  handleCoverImageMenuOutsideClick: (event: MouseEvent) => void;
  onClickDeleteCoverImage: () => Promise<void>;
  onClickEditGenreCategory: () => Promise<void>;
  toggleWorkStatusMenu: () => void;
  handleWorkStatusMenuKeydown: (event: KeyboardEvent) => void;
  handleWorkStatusMenuOutsideClick: (event: MouseEvent) => void;
  onClickWorkStatus: (item: NovelParam) => Promise<void>;
  closeWorkStatusHighlight: () => void;
}

interface Computed {
  novel?: Novel;
  imageKey: string;
  coverImageVersion: number;
  hasCoverImage: boolean;
  genreName?: string;
  categoryNames: { id: string; name: string }[];
  workStatusName?: string;
  currentWorkStatusId: string;
  workStatusButtonStyle: { "--status-bg": string; "--status-hover-bg": string };
  hasDisplayedWorkStatusHighlightPopup: boolean;
  workStatusIsNotCompleted: boolean;
  isAnyCardSaving: boolean;
  isTitleDescriptionSaving: boolean;
  isDeleteDisabled: boolean;
  novelIdFromNolaNovel?: string;
  user: User;
  isAgentFeatureNotAgreed: boolean;
  translatedGenreList: NovelParam[];
  translatedCategoryList: NovelParam[];
  translatedWorkStatusList: NovelParam[];
  saveTimeout: boolean;
  titleMaxLength: number;
  descriptionMaxLength: number;
  titleCounter: string;
  descriptionCounter: string;
  titleOrDescriptionChanged: boolean;
}

interface Props {
  novelId: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { ImageView, TextField, Onboarding, IconImagePlus, IconPencil, IconTrash, IconChevronDown, IconCheck },

  props: {
    novelId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      hasNolaNovelAccount: false,
      editableTitle: "",
      editableDescription: "",
      initialTitle: "",
      initialDescription: "",
      isTitleDescriptionInitialized: false,
      isComposingTitle: false,
      isComposingDescription: false,
      savePromise: null,
      isCoverImageMenuOpen: false,
      isCoverImageSaving: false,
      isGenreCategorySaving: false,
      isWorkStatusMenuOpen: false,
      isWorkStatusSaving: false,
      isNolaNovelDelinking: false,
      showFirstWorkStatusHighlight: false,
      unbindShortcutKeys: null,
    };
  },

  computed: {
    novel() {
      const { novelId } = this;
      const { getters } = this.$store;
      return getters["novelModule/novel"](novelId) as Novel;
    },
    translatedGenreList() {
      return getTranslatedGenreList(this.$t);
    },
    translatedCategoryList() {
      return getTranslatedCategoryList(this.$t);
    },
    translatedWorkStatusList() {
      return getTranslatedWorkStatusList(this.$t);
    },
    imageKey() {
      const { novel } = this;
      if (!novel) {
        // return placeholder path.
        return "/img/placeholders/novel.png";
      }

      const { novelId, image } = novel;
      if (!image) {
        // return placeholder path.
        return "/img/placeholders/novel.png";
      }

      if (image && image.startsWith("file:")) {
        return `file:novels/${novelId}/cover.jpg`;
      }

      return image;
    },
    coverImageVersion() {
      return this.$store.getters["novelModule/coverImageVersion"](this.novelId);
    },
    hasCoverImage() {
      return !!this.novel?.image;
    },
    genreName() {
      const { novel } = this;
      if (!novel || !novel.genre) {
        return undefined;
      }

      return getTranslatedGenreById(this.$t, novel.genre);
    },
    categoryNames() {
      const { novel } = this;
      if (!novel || !novel.category) {
        return [];
      }

      return getCategoryNameItems(this.$t, novel.category);
    },
    workStatusName() {
      const { novel } = this;
      if (!novel || !novel.workStatus) {
        return this.translatedWorkStatusList[0].name;
      }

      const statusName = getTranslatedWorkStatusById(this.$t, novel.workStatus);
      return statusName || this.translatedWorkStatusList[0].name;
    },
    currentWorkStatusId() {
      const { novel } = this;
      return (novel && novel.workStatus) || this.translatedWorkStatusList[0].id;
    },
    hasDisplayedWorkStatusHighlightPopup() {
      return this.$store.getters["generalModule/hasDisplayedWorkStatusHighlightPopup"];
    },
    workStatusIsNotCompleted() {
      return this.currentWorkStatusId !== "completed";
    },
    // 表紙画像・ジャンルカテゴリ・制作状況・Nolaノベル連携解除のいずれかが保存中かどうか。
    // flushIfEditable()とwatch.saveTimeout()の両方で同じ条件を使うため、判定基準がずれないよう1箇所にまとめる
    isAnyCardSaving() {
      return this.isCoverImageSaving || this.isGenreCategorySaving || this.isWorkStatusSaving || this.isNolaNovelDelinking;
    },
    // savePromiseがある間はタイトル・あらすじの自動保存が進行中（デバウンス完了後〜保存確定まで）。
    // titleOrDescriptionChangedは保存成功時にinitialTitle/initialDescriptionを更新した直後にfalseへ
    // 戻るため、savePromiseと合わせて見ることで保存確定直前の一瞬も確実に「保存中」として扱える
    isTitleDescriptionSaving() {
      return !!this.savePromise;
    },
    // タイトル・あらすじに未保存の変更がある間、または何らかの保存が進行中の間は、
    // 確認ダイアログに渡すタイトルが保存済みの値と一致しない恐れがあるため削除ボタンを非活性化する。
    // これによりdeleteButtonClickが呼ばれる時点では必ずtitleOrDescriptionChangedがfalseになるため、
    // そちら側でflushPendingChanges()を呼ぶ必要はない
    isDeleteDisabled() {
      return this.titleOrDescriptionChanged || this.isTitleDescriptionSaving || this.isAnyCardSaving;
    },
    // 背景色をCSSカスタムプロパティとして渡し、ホバー時の色変更はCSSの:hoverに任せる。
    // JS側でmouseover/mouseleaveの度にstyleを差し替えると、その度にコンポーネント全体が
    // 再レンダリングされてしまうため、状態に応じて変わらない値はCSS側に渡し切ってしまう
    workStatusButtonStyle() {
      const { currentWorkStatusId } = this;
      const id = currentWorkStatusId as WorkStatusId;
      return {
        "--status-bg": WORK_STATUS_COLORS[id] || WORK_STATUS_COLORS.inProgress,
        "--status-hover-bg": WORK_STATUS_HOVER_COLORS[id] || WORK_STATUS_HOVER_COLORS.inProgress,
      };
    },
    novelIdFromNolaNovel() {
      const { novel } = this;

      if (!novel) {
        return undefined;
      }

      const { associatedData } = novel;

      if (!associatedData) {
        return undefined;
      }

      const { nolaNovel } = associatedData;

      if (!nolaNovel) {
        return undefined;
      }

      const { id } = nolaNovel;

      if (!id) {
        return undefined;
      }

      return id;
    },
    user() {
      return this.$store.getters["userModule/user"];
    },
    isAgentFeatureNotAgreed() {
      return this.user.agentStartedAt === null;
    },
    saveTimeout() {
      return this.$store.getters["novelModule/saveTimeout"];
    },
    titleMaxLength() {
      return TITLE_MAX_LENGTH;
    },
    descriptionMaxLength() {
      return DESCRIPTION_MAX_LENGTH;
    },
    titleCounter() {
      return textCounter(this.editableTitle.length, this.titleMaxLength);
    },
    descriptionCounter() {
      return textCounter(this.editableDescription.length, this.descriptionMaxLength);
    },
    titleOrDescriptionChanged() {
      const { editableTitle, editableDescription, initialTitle, initialDescription } = this;
      // autoSaveTitleAndDescriptionは保存時にタイトルが空なら「タイトル未設定」を挿入するため、
      // タイトルを空にした操作自体も通常の変更として扱ってよい（保存後は必ず非空になり収束する）
      const titleChanged = editableTitle !== (initialTitle || "");
      const descriptionChanged = (editableDescription || "") !== (initialDescription || "");
      return titleChanged || descriptionChanged;
    },
  },

  watch: {
    // novel は SideMenu.vue が created() で非同期に取得する（novelModule/initialize）ため、
    // 詳細画面への直接アクセスやリロード時は本コンポーネントの created() 時点では
    // novel が未取得（undefined）のことがある。initialize() の同期処理だけに頼ると
    // タイトル・あらすじの編集用ローカル値が空のまま初期化されてしまい、その状態で入力すると
    // 保存時に既存のあらすじを空文字で上書きしてしまう。
    // そのため novel の取得を watch で待ち、取得できた時点で一度だけ初期化する
    // （以降 novel が更新されても、編集中の値を上書きしないようにする）。
    novel: {
      immediate: true,
      async handler(newNovel?: Novel) {
        if (newNovel && !this.isTitleDescriptionInitialized) {
          this.editableTitle = newNovel.title || "";
          this.editableDescription = newNovel.description || "";
          this.initialTitle = newNovel.title || "";
          this.initialDescription = newNovel.description || "";
          this.isTitleDescriptionInitialized = true;

          // 初回限定で制作状況バッジをハイライト表示する（完了済み作品には不要）
          if (!this.hasDisplayedWorkStatusHighlightPopup && this.workStatusIsNotCompleted) {
            this.showFirstWorkStatusHighlight = true;
            await this.$store.dispatch("generalModule/setDisplayedWorkStatusHighlightPopup", true);
          }
        }
      },
    },
    saveTimeout() {
      if (this.saveTimeout) {
        this.$store.dispatch("novelModule/stopSaveTimeout");

        // 表紙画像・ジャンルカテゴリ・制作状況・Nolaノベル連携解除のいずれかが保存中の場合、
        // ここでタイトル・あらすじのupdateNovelを実行すると互いに古いstate.novelsを元に
        // 更新してしまい後勝ちで一方の変更が失われ得るため、今回は保存せず見送る。
        // 編集内容は破棄せず、各保存処理のfinally(resumeAutoSaveIfNeeded)でタイマーを
        // 再始動し、その保存が完了した後に改めて自動保存を試みる
        if (this.isAnyCardSaving) {
          return;
        }

        this.autoSaveTitleAndDescription();
      }
    },
  },

  async created() {
    const { initialize } = this;
    initialize();

    // URL直接変更・リロード・タブを閉じるなどのフルページ遷移はvue-routerのガード
    // （beforeRouteLeave/beforeRouteUpdate）を経由しないため、flushによる自動保存が効かない。
    // このページではグローバルなsetModifiedを使わない方針にしたため、ネイティブの警告も
    // 別途ここで個別に出す必要がある
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("keydown", this.handleCoverImageMenuKeydown);
    window.addEventListener("keydown", this.handleWorkStatusMenuKeydown);
    // captureフェーズで登録し、途中の要素がstopPropagation()していても
    // 確実に外側クリックを検知できるようにする
    document.addEventListener("click", this.handleCoverImageMenuOutsideClick, true);
    document.addEventListener("click", this.handleWorkStatusMenuOutsideClick, true);

    // タイトル・あらすじは3秒デバウンスで自動保存されるが、他の編集画面と同様
    // Cmd/Ctrl+Sで即座に保存できるようにする（ブラウザ標準の保存ダイアログは抑止される）
    this.unbindShortcutKeys = handleShortcutKeys([
      { trigger: TriggerKey.Meta, keys: ["s"], callback: this.flushIfEditable },
      { trigger: TriggerKey.Ctrl, keys: ["s"], callback: this.flushIfEditable },
    ]);
  },

  beforeDestroy() {
    // 3秒のデバウンス完了前に画面を離れた場合、タイマーを止めないと
    // Vuex側のグローバルなsaveTimeoutがtrueのまま残り、以降の自動保存が動かなくなるため
    this.$store.dispatch("novelModule/stopSaveTimeout");
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("keydown", this.handleCoverImageMenuKeydown);
    window.removeEventListener("keydown", this.handleWorkStatusMenuKeydown);
    document.removeEventListener("click", this.handleCoverImageMenuOutsideClick, true);
    document.removeEventListener("click", this.handleWorkStatusMenuOutsideClick, true);
    if (this.unbindShortcutKeys) this.unbindShortcutKeys();
  },

  methods: {
    // initialize

    async initialize() {
      const { novel } = this;

      if (novel) {
        // エージェントに未同意状態で、作品完了にした場合ポップアップを表示
        if (this.isAgentFeatureNotAgreed && this.$route.query.workStatus === "completed") {
          const agentPromotionDialog = new Dialog(AgentPromotionDialog);
          agentPromotionDialog.show({ novelTitle: novel.title, useWorkCompletedDesign: true });
        }
      }

      try {
        const results = await new UserClient().checkConnectedOtherServices();
        const { nolaNovel } = results;
        if (nolaNovel) {
          this.hasNolaNovelAccount = nolaNovel.result ?? false;
        }
      } catch (e) {
        // 特にエラーでも何もしない
      }
    },

    // タイトル・あらすじの自動保存（執筆画面の自動保存ロジックを参照）

    onChangeTitleOrDescription() {
      // この画面は自動保存が仕様で、離脱時も常にflushして保存するため、
      // グローバルな「未保存の変更を破棄しますか」ダイアログの対象にはしない（setModifiedは呼ばない）。
      // mixin側のbeforeRouteLeaveがページ自身のflushより先に実行される都合上、
      // setModified(true)にすると「破棄」を選んでも直後のflushで保存されてしまい、意図と矛盾するため。

      // IME変換中はタイマーを開始しない（変換確定前の中間文字列が保存されるのを防ぐ）
      if (this.titleOrDescriptionChanged && !this.isComposingTitle && !this.isComposingDescription) {
        this.$store.dispatch("novelModule/startSaveTimeout");
      }
    },
    onCompositionStart(field) {
      if (field === "title") {
        this.isComposingTitle = true;
      } else {
        this.isComposingDescription = true;
      }
      this.$store.dispatch("novelModule/stopSaveTimeout");
    },
    onCompositionEnd(field) {
      if (field === "title") {
        this.isComposingTitle = false;
      } else {
        this.isComposingDescription = false;
      }
      this.onChangeTitleOrDescription();
    },
    async autoSaveTitleAndDescription() {
      // saveTimeoutのwatchはawaitせずに呼ぶため、その保存が完了する前にflushPendingChanges
      // （削除・Nolaノベル投稿・画面遷移）が呼ばれると二重に保存リクエストが飛びうる。
      // 進行中の保存があれば新規に開始せず、その結果を待つ
      if (this.savePromise) {
        const ok = await this.savePromise;
        if (!ok) {
          return false;
        }
        // 進行中の保存を待っている間にさらに編集が入っている可能性があるため、
        // 反映されていない差分が残っていれば改めて保存する
        if (!this.titleOrDescriptionChanged) {
          return true;
        }
        return this.autoSaveTitleAndDescription();
      }

      const run = async (): Promise<boolean> => {
        const { novelId, editableDescription } = this;

        // 削除等でストアから作品自体がなくなっている場合、保存対象が存在しない。
        // このままupdateNovelを呼ぶと「Novel not found」で失敗し続け、
        // 離脱時のnext(false)によって画面から出られなくなるため、何もせず成功扱いにする
        if (!this.novel) {
          return true;
        }

        if (!this.titleOrDescriptionChanged) {
          return true;
        }

        // タイトルが未入力のまま保存されるタイミングでは、空文字ではなく「タイトル未設定」を挿入して保存する。
        // 保存が失敗した場合に画面上のタイトルだけ書き換わってしまわないよう、this.editableTitleへの反映は
        // 保存成功後に行い、リクエストにはローカル変数のtitleToSaveを使う
        const wasTitleEmptyAtStart = !this.editableTitle.trim();
        const titleToSave = this.editableTitle.trim() ? this.editableTitle : this.$t("novelDetail.untitled").toString();

        try {
          const result = await this.$store.dispatch("novelModule/updateNovel", {
            novelId,
            title: titleToSave,
            description: editableDescription,
          });

          if (!result) {
            // GraphQLがエラーを含む場合等、実際には保存されていないため成功扱いにしない
            // （エラー内容はGraphQLClient側の共通ダイアログで既に表示されている）
            return false;
          }

          // 保存中（await中）にユーザーがタイトルを入力していた場合、その入力を巻き戻さないよう
          // 「保存開始時点で空」かつ「現在も空」の場合のみ挿入したプレースホルダーを反映する
          if (wasTitleEmptyAtStart && !this.editableTitle.trim()) {
            this.editableTitle = titleToSave;
          }
          this.initialTitle = titleToSave;
          this.initialDescription = editableDescription;

          this.$notify({
            title: this.$t("common.saved"),
            type: "success",
            duration: 2000,
          });
          return true;
        } catch (e) {
          // 通信エラー等は updateNovelの呼び出し元であるGraphQLClient側で
          // 既にエラーダイアログの表示・ログ送信（log.error）が行われているため、ここでは重複させない
          return false;
        }
      };

      this.savePromise = run();
      try {
        return await this.savePromise;
      } finally {
        this.savePromise = null;
      }
    },
    async flushPendingChanges() {
      if (this.titleOrDescriptionChanged) {
        this.$store.dispatch("novelModule/stopSaveTimeout");
        return this.autoSaveTitleAndDescription();
      }
      return true;
    },
    // Cmd/Ctrl+Sのショートカット用。表紙画像・ジャンルカテゴリ・制作状況のいずれかを保存中は、
    // その保存処理と無関係なタイトル・あらすじのflushを割り込ませない
    flushIfEditable() {
      if (this.isAnyCardSaving) return;
      this.flushPendingChanges();
    },
    // 表紙画像・ジャンルカテゴリ・制作状況・Nolaノベル連携解除の各保存処理のfinallyから呼ぶ。
    // それらの保存中はデバウンス経由の自動保存(watch.saveTimeout)を見送っているため、
    // 未保存のタイトル・あらすじが残っていればここでタイマーを再始動し、後追いで保存させる
    resumeAutoSaveIfNeeded() {
      if (this.titleOrDescriptionChanged) {
        this.$store.dispatch("novelModule/startSaveTimeout");
      }
    },
    handleBeforeUnload(event: BeforeUnloadEvent) {
      // URLの直接変更・リロード・タブを閉じる操作はvue-routerを経由しないためflushできない。
      // 未保存の変更が残っている場合のみ、ブラウザネイティブの離脱確認を出す
      if (!this.titleOrDescriptionChanged) {
        return;
      }
      // eslint-disable-next-line no-param-reassign
      event.returnValue = this.$t("common.unsavedChanges.message") as string;
    },

    // 表紙画像設定

    toggleCoverImageMenu() {
      this.isCoverImageMenuOpen = !this.isCoverImageMenuOpen;
      if (this.isCoverImageMenuOpen) {
        this.$nextTick(() => {
          const menu = this.$refs.coverImageMenu as HTMLElement | undefined;
          getInitialMenuFocusTarget(menu, 0)?.focus();
        });
      }
    },
    handleCoverImageMenuKeydown(event: KeyboardEvent) {
      if (!this.isCoverImageMenuOpen) return;
      if (event.key === "Escape") {
        this.isCoverImageMenuOpen = false;
        (this.$refs.coverImageMenuButton as HTMLElement | undefined)?.focus();
        return;
      }
      handleMenuNavigationKeydown(event, this.$refs.coverImageMenu as HTMLElement | undefined);
    },
    // z-indexが高い要素(サイドメニュー等)の上でクリックすると、透明なbackdrop要素では
    // クリックを奪われてメニューが閉じないことがあったため、document側のclickで判定する。
    // これによりDOM上の重なり順に関係なく、メニュー外クリックで確実に閉じられる
    handleCoverImageMenuOutsideClick(event: MouseEvent) {
      if (!this.isCoverImageMenuOpen) return;
      const wrapper = this.$refs.coverImageWrapper as HTMLElement | undefined;
      if (isOutsideWrapper(wrapper, event.target)) {
        this.isCoverImageMenuOpen = false;
      }
    },
    async onClickChangeCoverImage() {
      this.isCoverImageMenuOpen = false;
      if (this.isCoverImageSaving) return;
      const { novelId } = this;
      // 削除処理と競合して片方の更新が消えることを防ぐため、flushPendingChanges()の待ち時間中も
      // 含めてメニュー操作不可にする(flush完了後にガードすると、await中はまだ操作可能で競合し得るため)
      this.isCoverImageSaving = true;
      try {
        // タイトル・あらすじの自動保存がデバウンス待ち中に本ダイアログのupdateNovelが先に走ると、
        // 互いに古いstate.novelsを元に更新してしまい後勝ちで一方の変更が失われ得るため、先に確定させる。
        // 保存に失敗した場合、そのまま続行すると「タイトル保存失敗」に気づかないまま表紙だけ更新できてしまうため中断する
        if (!(await this.flushPendingChanges())) {
          return;
        }
        const dialog = new Dialog((CoverImageSettingDialog as unknown) as Component<object, any>);
        const result = await dialog.show({ novelId });
        if (result) {
          // imageの値が"file:"始まりだと常に同じURLに解決されるため、
          // 再アップロード時に表示が更新されるようkeyを変えて強制的に再マウントする（サイドメニューのサムネイルとも共有）
          this.$store.commit("novelModule/incrementCoverImageVersion", novelId);
        }
      } finally {
        this.isCoverImageSaving = false;
        this.resumeAutoSaveIfNeeded();
      }
    },
    async onClickDeleteCoverImage() {
      this.isCoverImageMenuOpen = false;
      const { novelId, novel } = this;
      if (!novel || this.isCoverImageSaving) return;

      // 元に戻せない操作のため、削除前に確認する
      const confirmDialog = new Dialog(CoverImageDeleteConfirmDialog);
      const confirmed = await confirmDialog.show({
        title: this.$t("novelDetail.coverImageSetting.deleteConfirmTitle"),
        content: this.$t("novelDetail.coverImageSetting.deleteConfirmMessage"),
        okButton: this.$t("novelDetail.coverImageSetting.deleteButton"),
      });
      if (!confirmed) return;

      // 削除処理中に「画像を変更」から新しい表紙のアップロードが完了すると、後着の本処理が古い状態を
      // 元にnullを送ってしまい新しい表紙が消えるため、flushPendingChanges()の待ち時間中も含めて
      // メニュー操作不可にする(flush完了後にガードすると、await中はまだ操作可能で競合し得るため)
      this.isCoverImageSaving = true;
      try {
        // タイトル・あらすじの自動保存がデバウンス待ち中に本処理のupdateNovelが先に走ると、
        // 互いに古いstate.novelsを元に更新してしまい後勝ちで一方の変更が失われ得るため、先に確定させる。
        // 保存に失敗した場合、そのまま続行すると「タイトル保存失敗」に気づかないまま表紙だけ削除できてしまうため中断する
        if (!(await this.flushPendingChanges())) {
          return;
        }
        // flushPendingChanges()の待ち時間中に作品がstoreから消えている場合、updateNovel側は
        // GraphQLClientを経由しない同期的なthrowになり、catch節での「既に表示済み」の前提が
        // 成り立たず通知なしで失敗してしまうため、dispatch直前にストア上の存在を再確認する
        if (!this.$store.getters["novelModule/novel"](novelId)) {
          this.$notify({
            title: this.$t("novelDetail.coverImageSetting.saveFailed"),
            type: "error",
          });
          return;
        }
        // 画像削除は他画面(ImageSelector.vue)の前例に合わせ、空文字ではなくnullで送る
        const updated = await this.$store.dispatch("novelModule/updateNovel", {
          novelId,
          image: null,
        });
        if (!updated) {
          // GraphQLがエラーを含む場合等、実際には保存されていないため成功扱いにしない
          // （エラー内容はGraphQLClient側の共通ダイアログで既に表示されている）
          return;
        }
        if (novel.image && novel.image.startsWith("file:")) {
          // S3側の実体もあわせて削除する。DB更新は既に成功しているため、
          // ここで失敗してもユーザー操作としては成功扱いとし、通知は出さない（表示上はimage未設定のため実害はない）
          try {
            await deleteBinary(`novels/${novelId}/cover.jpg`);
          } catch (error) {
            // 失敗してもDB更新は成功しているため処理は継続する
          }
        }
        this.$store.commit("novelModule/incrementCoverImageVersion", novelId);
        this.$notify({
          title: this.$t("common.deleted"),
          type: "success",
          duration: 2000,
        });
      } catch (error) {
        // 通信エラー等は updateNovelの呼び出し元であるGraphQLClient側で
        // 既にエラーダイアログの表示・ログ送信が行われているため、ここでは重複させない
      } finally {
        this.isCoverImageSaving = false;
        this.resumeAutoSaveIfNeeded();
      }
    },
    async onClickEditGenreCategory() {
      const { novelId, novel } = this;
      // このボタン自体がv-if="novel"配下にありnovel未取得時はDOMに存在しないが、
      // deleteButtonClick等の既存メソッドと同様、念のため防御的にガードしておく
      if (!novel || this.isGenreCategorySaving) {
        return;
      }
      // 連打でダイアログが多重に開くと、下に隠れた古いダイアログでも保存でき、
      // 後から確定した古い選択内容で上書きされてしまうため、flushPendingChanges()の
      // 待ち時間中も含めてボタンを無効化する
      this.isGenreCategorySaving = true;
      try {
        // タイトル・あらすじの自動保存がデバウンス待ち中に本ダイアログのupdateNovelが先に走ると、
        // 互いに古いstate.novelsを元に更新してしまい後勝ちで一方の変更が失われ得るため、先に確定させる。
        // 保存に失敗した場合、そのまま続行すると「タイトル保存失敗」に気づかないままジャンル・カテゴリだけ
        // 更新できてしまうため中断する
        if (!(await this.flushPendingChanges())) {
          return;
        }
        const dialog = new Dialog((GenreCategorySettingDialog as unknown) as Component<object, any>);
        await dialog.show({ novelId });
      } finally {
        this.isGenreCategorySaving = false;
        this.resumeAutoSaveIfNeeded();
      }
    },

    // 制作状況

    toggleWorkStatusMenu() {
      this.isWorkStatusMenuOpen = !this.isWorkStatusMenuOpen;
      if (this.isWorkStatusMenuOpen) {
        const selectedIndex = this.translatedWorkStatusList.findIndex((item) => item.id === this.currentWorkStatusId);
        this.$nextTick(() => {
          const menu = this.$refs.workStatusMenu as HTMLElement | undefined;
          getInitialMenuFocusTarget(menu, Math.max(selectedIndex, 0))?.focus();
        });
      }
    },
    handleWorkStatusMenuKeydown(event: KeyboardEvent) {
      if (!this.isWorkStatusMenuOpen) return;
      if (event.key === "Escape") {
        this.isWorkStatusMenuOpen = false;
        (this.$refs.workStatusButton as HTMLElement | undefined)?.focus();
        return;
      }
      handleMenuNavigationKeydown(event, this.$refs.workStatusMenu as HTMLElement | undefined);
    },
    handleWorkStatusMenuOutsideClick(event: MouseEvent) {
      if (!this.isWorkStatusMenuOpen) return;
      const wrapper = this.$refs.workStatusWrapper as HTMLElement | undefined;
      if (isOutsideWrapper(wrapper, event.target)) {
        this.isWorkStatusMenuOpen = false;
      }
    },
    async onClickWorkStatus(item: NovelParam) {
      this.isWorkStatusMenuOpen = false;
      (this.$refs.workStatusButton as HTMLElement | undefined)?.focus();
      const { novelId, novel, currentWorkStatusId } = this;
      if (!novel || item.id === currentWorkStatusId || this.isWorkStatusSaving) return;

      // 連打・多重クリックで2件の制作状況更新が並行して走ると、後着の方に古い状態を
      // 元にした更新が上書きされ得るため、flushPendingChanges()の待ち時間中も含めて
      // ボタンを無効化する（表紙・ジャンルカテゴリの各編集ボタンと同じガード）
      this.isWorkStatusSaving = true;
      try {
        // タイトル・あらすじの自動保存がデバウンス待ち中に本処理のupdateNovelが先に走ると、
        // 互いに古いstate.novelsを元に更新してしまい後勝ちで一方の変更が失われ得るため、先に確定させる。
        // 保存に失敗した場合、そのまま続行すると「タイトル保存失敗」に気づかないまま制作状況だけ更新できてしまうため中断する
        if (!(await this.flushPendingChanges())) {
          return;
        }

        // flushPendingChanges()の待ち時間中に作品がstoreから消えている場合、updateNovel側は
        // GraphQLClientを経由しない同期的なthrowになり、catch節での「既に表示済み」の前提が
        // 成り立たず通知なしで失敗してしまうため、dispatch直前にストア上の存在を再確認する
        if (!this.$store.getters["novelModule/novel"](novelId)) {
          this.$notify({
            title: this.$t("novelDetail.workStatusSaveFailed"),
            type: "error",
          });
          return;
        }

        const updated = await this.$store.dispatch("novelModule/updateNovel", {
          novelId,
          workStatus: item.id,
        });
        if (!updated) {
          // GraphQLがエラーを含む場合等、実際には保存されていないため成功扱いにしない
          // （エラー内容はGraphQLClient側の共通ダイアログで既に表示されている）
          return;
        }
        this.$notify({
          title: this.$t("common.saved"),
          type: "success",
          duration: 2000,
        });
        // エージェントに未同意状態で作品を完了にした場合、編集画面経由の保存と同様に
        // エージェント機能の促進ダイアログを表示する（initialize()内の?workStatus=completed判定と挙動を揃える）
        // flushPendingChanges()でタイトルが更新されている可能性があるため、ここでstoreから読み直す
        if (item.id === "completed" && this.isAgentFeatureNotAgreed) {
          const agentPromotionDialog = new Dialog(AgentPromotionDialog);
          agentPromotionDialog.show({ novelTitle: this.novel?.title || novel.title, useWorkCompletedDesign: true });
        }
      } catch (error) {
        // 通信エラー等は updateNovelの呼び出し元であるGraphQLClient側で
        // 既にエラーダイアログの表示・ログ送信が行われているため、ここでは重複させない
      } finally {
        this.isWorkStatusSaving = false;
        this.resumeAutoSaveIfNeeded();
      }
    },
    closeWorkStatusHighlight() {
      this.showFirstWorkStatusHighlight = false;
    },

    // event handle (button, checkbox, etc)

    deleteButtonClick() {
      const { novel, showNovelDeleteConfirmDialog } = this;
      if (!novel || this.isDeleteDisabled) {
        return;
      }

      // isDeleteDisabledがfalseの時点でtitleOrDescriptionChangedもfalse（保存済み）であることが
      // 保証されているため、ここで改めてflushする必要はない
      showNovelDeleteConfirmDialog(novel.title || "");
    },
    async postToNolaNovelButtonClick(isPost) {
      const { showNolaNovelListDialog, novelIdFromNolaNovel, showExportConfirmDialog } = this;

      // Nolaノベルへの投稿・上書きは最新のタイトル・あらすじを対象にしたいため、
      // 未保存の変更があれば先に確定させる。保存に失敗した場合、古い内容のまま
      // 投稿されてしまうのを防ぐため後続処理を中断する
      // （エラー内容はGraphQLClient側の共通ダイアログで既に表示されている）
      const flushed = await this.flushPendingChanges();
      if (!flushed) {
        return;
      }

      if (novelIdFromNolaNovel) {
        showExportConfirmDialog(novelIdFromNolaNovel);
        return;
      }

      showNolaNovelListDialog(isPost);
    },
    async delinkNolaNovelButtonClick() {
      const { novelIdFromNolaNovel, isNolaNovelDelinking } = this;
      if (!novelIdFromNolaNovel || isNolaNovelDelinking) {
        return;
      }

      // 連打・多重クリックでflushPendingChanges()の待ち時間中に再度呼ばれるのを防ぐため、
      // 表紙画像等の各保存処理と同様にflushPendingChanges()の待ち時間中も含めてガードする
      this.isNolaNovelDelinking = true;
      try {
        // 連携解除とタイトル・あらすじの自動保存が競合しないよう、先に未保存の変更を確定させる。
        // 保存に失敗した場合は中断する
        if (!(await this.flushPendingChanges())) {
          return;
        }

        await this.showNolaNovelDelinkConfirmDialog(novelIdFromNolaNovel);
      } finally {
        this.isNolaNovelDelinking = false;
        this.resumeAutoSaveIfNeeded();
      }
    },

    // view

    async showNolaNovelListDialog(isPost) {
      const { novelId, hasNolaNovelAccount } = this;

      if (!hasNolaNovelAccount) {
        const introduction = new Dialog(NolaNovelIntroduction);
        introduction.show();
        return;
      }

      const novelListDialog = new Dialog(NovelListDialog);
      const data: NovelListDialogProps = {
        novelId,
        isPost,
      };

      const result = await novelListDialog.show(data);
      if (result) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const data: CupertinoAlertDialogProps = {
          title: this.$t("novelDetail.linkComplete.title"),
          content: this.$t("novelDetail.linkComplete.content"),
          action: this.$t("novelDetail.linkComplete.closeButton"),
        };
        completeDialog.show(data);
      }

      const { initialize } = this;
      initialize();
    },
    async showExportConfirmDialog(id) {
      const { novel, navigateToNolaNovelEditPage } = this;
      if (!novel) {
        return;
      }

      const confirmDialog = new Dialog(NolaNovelUpdateConfirm);
      const data: NolaNovelUpdateConfirmProps = {
        id,
        content: this.$t("novelDetail.updateConfirm.content"),
      };
      const result = await confirmDialog.show(data);
      if (result) {
        navigateToNolaNovelEditPage(id);
      }
    },
    async showNolaNovelDelinkConfirmDialog(id) {
      const { novelId, initialize } = this;
      const delinkDialog = new Dialog(NolaNovelDelinkConfirm);
      const data: NolaNovelDelinkConfirmProps = {
        novelId,
        nolaNovelId: id,
      };
      const result = await delinkDialog.show(data);
      if (result) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const completeData: CupertinoAlertDialogProps = {
          title: this.$t("novelDetail.unlinkComplete.title"),
          content: this.$t("novelDetail.unlinkComplete.content"),
          action: this.$t("novelDetail.unlinkComplete.closeButton"),
        };
        await completeDialog.show(completeData);
        initialize();
      }
    },
    async showNovelDeleteConfirmDialog(title) {
      const { novelId, novelIdFromNolaNovel } = this;
      const confirmDialog = new Dialog(NovelDeleteConfirm);
      const data: NovelDeleteConfirmProps = {
        novelId,
        nolaNovelId: novelIdFromNolaNovel,
        title,
      };

      const result = await confirmDialog.show(data);

      if (result) {
        this.$notify({
          title: this.$t("novelDetail.deleteComplete.message"),
          type: "error",
        });

        this.$router.push({ name: "home" });
      }
    },
    async navigateToNolaNovelEditPage(id: string) {
      const url = process.env.VUE_APP_NOLANOVEL_WEB;

      if (!url) {
        throw new Error(this.$t("novelDetail.nolaNovelUrlError"));
      }

      const { novelId } = this;

      const service = new NolaNovelUrlGenerator(process.env.VUE_APP_NOLANOVEL_WEB!);

      const updateNovelUrl = await service.genUpdateNovelUrl(id, novelId);
      window.open(
        createUrlWithUtmParams(updateNovelUrl, NolaPageName.Detail, NolaItemId.ConfirmOverwriteNovelButton),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

// 表紙画像・その下のジャンル・カテゴリカードの幅。info-txt側のcalc()もこの値との関係で決まる
$cover-width: 256px;
// SPでの表紙画像の幅(高さは256:380比率をほぼ保つ172:255)
$cover-width-sp: 172px;

.flex-row {
  display: flex;
  flex-direction: row;
}

.novel-detail {
  display: flex;
  margin: $margin-l;
  flex-wrap: wrap;
  justify-content: center;

  @include mq(sp) {
    margin: $margin-s;
    // SPでは表紙画像→制作状況バッジ→タイトル/あらすじ→カテゴリ/ジャンルの順に
    // 縦一列で並べたいが、DOM上はバッジ~あらすじ(.info-txt)とカテゴリ/ジャンル
    // (.genre-category-card)が別の親(.info-txt/.info-img)に分かれているため、
    // 単純な折り返しでは順序を入れ替えられない。1列のflexコンテナにし、
    // .info-imgをdisplay:contentsで透過させて中の要素を直接のflex itemにし、
    // orderで並び順を制御する。
    // flex-direction:columnにすると、主軸が縦になりjustify-content:centerが
    // 横方向のセンタリングとして効かなくなる(縦方向の余白配分に変わり、高さが
    // 内容に合わせて伸縮するこのコンテナでは実質無意味になる)ため、横方向の
    // センタリングはalign-itemsで明示的に指定し直す。.info-txtはwidth:100%で
    // 幅いっぱいに広がるため、align-items:centerを設定してもcenter基準の見た目には影響しない
    flex-direction: column;
    align-items: center;
  }
}

/*小説の表紙部分のレイアウト*/
.info-img {
  width: $cover-width;
  text-align: left;

  @include mq(sp) {
    // 自身はdisplay:contentsで消え、cover-image-wrapper/genre-category-cardが
    // .novel-detailの直接のflex itemとしてorder通りに並ぶ
    display: contents;
  }
}

.info-img .novel-cover-image {
  // 幅は上限のみ固定し、高さは固定値ではなく元の256:380比率をaspect-ratioで維持することで、
  // 親要素の幅に応じて縦横比を保ったまま縮小できるようにする
  width: 100%;
  max-width: $cover-width;
  aspect-ratio: 256 / 380;
  box-shadow: var(--shadow-sm);

  @include mq(sp) {
    max-width: $cover-width-sp;
    aspect-ratio: 172 / 255;
  }
}

.cover-image-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: $cover-width;

  @include mq(sp) {
    // 表紙画像を先頭に表示する。.info-imgが display:contents になり
    // 自身のpadding-topが効かなくなるため、上の余白はここに移す
    order: 1;
    margin-top: 15px;
    max-width: $cover-width-sp;
  }
}

.cover-image-edit-button {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-rounded);
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
  box-shadow: var(--shadow-sm);
  cursor: pointer;

  @include hover-overlay;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.cover-image-edit-icon {
  font-size: var(--font-size-md);
}

// 表紙画像メニュー・制作状況メニューで共通のポップオーバー/メニュー項目の見た目。
// 位置(top/left/right)とmin-widthだけがメニューごとに異なるため、それ以外を共通化する
%menu-popover {
  position: absolute;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  padding: 4px 0;
  z-index: 10;
  white-space: nowrap;
}

%menu-item {
  position: relative;
  @include hover-overlay;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  cursor: pointer;
  font-size: var(--font-size-xs);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &::after {
      display: none;
    }
  }
}

.cover-image-menu-popover {
  @extend %menu-popover;
  top: 26px;
  right: -10px;
  min-width: 160px;
}

.cover-image-menu-item {
  @extend %menu-item;

  &.cover-image-menu-item-danger {
    color: var(--color-warning);
  }
}

.cover-image-menu-item-icon {
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

/*小説の説明部分のレイアウト*/
.info-txt {
  // 表紙側の幅($cover-width) + 左右の余白(30px)
  width: calc(100% - #{$cover-width} - 30px);
  padding-left: 30px;

  @include mq(sp) {
    // SPでは表紙画像の右ではなく下に回り込むため、右並び用の左余白(30px)は不要。
    // .contentの`<hr>`(padding:15pxのみで幅が決まる)と横幅を揃えるため、
    // .novel-detailのmargin($margin-s)に自身のpaddingを足して合計15pxになるようにする。
    // width:100%とpaddingを両立させるためbox-sizingをborder-boxにする(合計幅が親を超えないように)
    box-sizing: border-box;
    width: 100%;
    padding: 0 (15px - $margin-s);
    // 表紙画像の次(バッジ→タイトル→あらすじの順は内部のDOM順のまま)に表示する
    order: 2;
  }
}

.info-txt h2 {
  font-size: var(--font-size-xs);
  color: var(--color-text-normal);
  font-weight: var(--font-weight-regular);
  margin: 1rem 0 0 0;
}

.info-txt h3 {
  color: var(--color-text-normal);
}

.info-txt h4 {
  color: var(--color-text-normal);
}

.info-txt span {
  color: var(--color-text-normal);
}

.info-txt a {
  color: var(--color-primary);
}

.field-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  .counter {
    font-size: var(--font-size-xs);
    color: var(--color-text-low);
  }
}

.detail-title-field,
.detail-description-field {
  // 上部のh2見出しが既に視覚的なラベルとして機能しているため、
  // TextFieldが生成する<label>は視覚的には隠しつつ、h2とフィールドをfor/idで
  // 関連付けるアクセシビリティ上の効果だけを残す
  ::v-deep .label {
    // TextField自身の .text-field .label { width: 200px } と詳細度が同等で
    // 読み込み順に結果が左右されるため、!importantで確実に上書きする
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }
}

.detail-title-field {
  --text-field-bg: var(--color-surface-muted);
  --text-field-border: none;
  margin-bottom: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);

  ::v-deep .input-text-group {
    padding: 10px;
    border-radius: var(--radius-xs);
  }
}

.detail-description-field {
  --text-field-bg: var(--color-surface-muted);
  --text-field-border: none;
  margin-bottom: 0;
  font-size: var(--font-size-xs);

  ::v-deep .input-text-group {
    padding: 15px;
    border-radius: var(--radius-xs);
  }
}

.work-status-wrapper {
  position: relative;
  display: inline-block;
  margin: 20px 0 0 0;

  @include mq(sp) {
    // inline-blockのままmargin:autoで中央寄せにする(text-align:centerを親に
    // 掛けると他の要素にも影響するため)。ポップアップメニューはこの要素からの
    // 相対位置(position:relative)なので、ボタンの実際の幅のまま中央寄せしても
    // 表示位置はボタンの直下からズレない
    display: block;
    width: fit-content;
    margin: 20px auto 0;
  }
}

.work-status-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  color: var(--color-text-on-fixed-bg);
  border: none;
  padding: 0 12px;
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
  cursor: pointer;
  user-select: none;
  // 背景色は:style経由で渡された--status-bg/--status-hover-bgを使う。JSでmouseover/mouseleaveの
  // 度にstyleを差し替えるとその都度コンポーネント全体が再レンダリングされてしまうため、
  // ホバー時の切り替え自体はCSSの:hoverに任せる
  background-color: var(--status-bg);

  &:hover {
    background-color: var(--status-hover-bg);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.work-status-button .work-status-name {
  // .info-txt span { color: var(--color-text-normal); } の方が詳細度(クラス+要素)で勝ってしまうため、
  // 単一クラスの.work-status-nameでは上書きできない。親要素とのクラス2つ指定にして詳細度で勝たせる
  color: var(--color-text-on-fixed-bg);
}

.work-status-chevron {
  width: 10px;
  height: 10px;
  font-size: var(--font-size-xxs);
}

.work-status-menu-popover {
  @extend %menu-popover;
  top: calc(100% + 4px);
  left: 0;
  min-width: 140px;
}

.work-status-menu-item {
  @extend %menu-item;
}

.work-status-menu-item-check {
  font-size: var(--font-size-md);
  flex-shrink: 0;
  color: var(--color-primary);
}

.work-status-menu-item-check-spacer {
  display: inline-block;
  width: var(--font-size-md);
  flex-shrink: 0;
}

.genre-category-card {
  // box-sizingを指定しないとpadding分だけ画像($cover-width)より横幅が広くなってしまうため、
  // widthにpaddingを含める指定にして画像と外枠の幅を正確に揃える
  box-sizing: border-box;
  width: 100%;
  max-width: $cover-width;
  margin-top: 12px;
  padding: 12px;
  border-radius: var(--radius-xs);
  background-color: var(--color-surface-muted);
  text-align: left;

  @include mq(sp) {
    // SPではタイトル・あらすじ(.info-txt)の後、最後に表示する。
    // 横幅もmax-widthの制限を外し、タイトル・あらすじ入力欄と同じ見た目の横幅
    // (.contentの`<hr>`と揃う、.novel-detailのmargin($margin-s)込みで15px)に揃える
    order: 3;
    max-width: none;
    width: calc(100% - #{15px - $margin-s} * 2);
    margin: 12px (15px - $margin-s) 0;
  }
}

.genre-category-row {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .genre-category-label {
    margin-top: 0;
  }
}

.genre-category-label {
  margin-top: 12px;
  margin-bottom: 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
}

.genre-category-edit-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-normal);
  cursor: pointer;

  @include hover-overlay;
}

.genre-category-edit-icon {
  font-size: var(--font-size-md);
}

.genre-category-value {
  margin-top: 4px;
  color: var(--color-text-normal);
  font-size: var(--font-size-xs);
}

.category-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.category-pill {
  padding: 4px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-rounded);
  color: var(--color-text-invert);
  font-size: var(--font-size-xs);
  background-color: var(--color-background-invert);
}

/*小説の削除説明部分のレイアウト*/

.button {
  padding: 10px 50px;
  margin-top: $margin-l;
  border-radius: var(--radius-xs);
  cursor: pointer;
}

.content {
  margin: $margin-l;
  padding-bottom: 24px;
  color: var(--color-text-normal);

  @include mq(sp) {
    margin: 0;
    padding: 15px;
    padding-bottom: 100px;
    background-color: var(--color-background-normal);
  }
}

p {
  background-color: var(--color-surface-subtle);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  padding: 15px;
  color: var(--color-text-normal);
}

// line

.dotted {
  border-style: dotted;
  border-left: none;
  border-right: none;
  border-bottom: none;
}

// color

.light-gray {
  color: var(--color-text-low);
  border-color: var(--color-text-low);
}

// margin

.mt-1 {
  margin-top: 1rem;
}

// flex

.flex {
  display: flex;
}

.flex-row {
  flex-direction: row;
}

.flex-column {
  flex-direction: column;
}

.gap-harf {
  gap: 0.5rem;
}

// button

.btn {
  padding: 0.5rem 1.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-rounded);
  color: var(--color-text-normal);

  &.post {
    color: var(--color-text-on-fixed-bg) !important;
    background-color: var(--color-primary);
    border-color: var(--color-primary);

    span {
      color: var(--color-text-on-fixed-bg) !important;
    }
  }

  &.delete {
    // 削除ボタンのみ実際の<button>要素にしているため、ブラウザ既定のフォント・背景をリセットする
    // (post/linkは従来通りdivのため対象外。background-colorは直後で上書きするためtransparentは不要。
    // fontショートハンドはfont-weightも上書きし、詳細度が.boldより高いこのセレクタで指定すると
    // 太字が効かなくなるため、font-weightを含まない個別プロパティで指定する)
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    appearance: none;
    -webkit-appearance: none;
    color: var(--color-text-on-fixed-bg);
    background-color: var(--color-warning);
    border-color: var(--color-warning);

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.link {
    border: none;
    background-color: var(--color-surface-muted);
  }
}

.pointer {
  cursor: pointer;
}

// text

.bold {
  font-weight: var(--font-weight-bold);
}

// container

.assosiate-container {
  margin: 1rem 1.5rem;

  @include mq(sp) {
    // 左右マージンをなくし、.contentのpadding(15px)だけで幅を決める。
    // これによりタイトル・あらすじ・カテゴリカード・`<hr>`と横幅が揃う
    margin: 1rem 0;
  }

  h3,
  h4,
  span {
    color: var(--color-text-normal);
  }

  a {
    color: var(--color-primary);
  }
}

.delete-container {
  margin: 1rem 1.5rem;

  @include mq(sp) {
    // 左右マージンをなくし、.contentのpadding(15px)だけで幅を決める。
    // これによりタイトル・あらすじ・カテゴリカード・`<hr>`と横幅が揃う
    margin: 1rem 0;
  }

  h3,
  span {
    color: var(--color-text-normal);
  }
}
</style>
